import { AxiosError } from 'axios'
import { isSafeInternalPath, pagesPaths } from 'common/router/routes.utils'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { userAccountStatusEnum } from 'services/auth/auth.enums'
import { IUser } from 'services/auth/auth.types'
import { useSWRConfig } from 'swr'
import useSWRMutation, { MutationFetcher } from 'swr/mutation'

interface IUseSocialLoginFlowOptions<TPayload> {
  /**
   * Raw `redirect` query value (as read from `router.query`, matching LoginComponent's
   * own pattern). When present and the user isn't pending verification, navigation goes
   * here instead of `pagesPaths.tests`. Leave unset for flows with no redirect concept
   * (e.g. the candidate sign-up wizard).
   */
  redirectTo?: string
  /** Provider-specific copy for the catch-all error toast, e.g. "Google sign-in failed. Please try again." */
  genericErrorMessage: string
  /**
   * Extra error handling layered before the generic catch-all (e.g. Apple's 401
   * "did not share your email" case). Return `true` if the error was handled
   * (and no further toast should be shown).
   */
  extraErrorHandler?: (err: AxiosError) => boolean
}

export const useSocialLoginFlow = <TPayload>(
  mutationKey: string,
  fetcher: MutationFetcher<IUser, TPayload, string>,
  options: IUseSocialLoginFlowOptions<TPayload>,
) => {
  const router = useRouter()
  const { mutate } = useSWRConfig()

  const { trigger, isMutating } = useSWRMutation(mutationKey, fetcher, {
    onError(err) {
      if (err instanceof AxiosError && err.response?.status === 403) {
        toast.error('Social login is available for seafarers only. Please use email + password.')
        return
      }
      if (err instanceof AxiosError && options.extraErrorHandler?.(err)) {
        return
      }
      toast.error(options.genericErrorMessage)
    },
    async onSuccess(user) {
      await mutate(() => true, undefined, { revalidate: false })
      if (user.status === userAccountStatusEnum.pendingVerification) {
        router.push(`${pagesPaths.verifyAccount}?email=${encodeURIComponent(user.email)}`)
        return
      }
      if (options.redirectTo && isSafeInternalPath(options.redirectTo)) {
        // Mirrors LoginComponent's password-login path, which uses `replace` (not `push`)
        // for the redirect-query branch.
        router.replace(decodeURIComponent(options.redirectTo))
        return
      }
      router.push(pagesPaths.tests)
    },
  })

  return { trigger, isMutating }
}
